import { useFormContext } from 'react-hook-form';
import type { CreativesByHypothesis } from '../api';
import { useCreatives, useCreativesWithHypothesis } from '../api';
import type { CreativeEntry, PackEntry } from 'types/shared';
import { queryClient } from 'lib/react-query';
import queryKeys from '../api/queryKeys';
import { useEffect, useMemo, useState } from 'react';

const isActivePackIndex = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const shallowEqualStrings = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const sameCreativeRowsById = (
  a: readonly CreativeEntry[],
  b: readonly CreativeEntry[],
): boolean =>
  a.length === b.length && a.every((row, i) => row?.id === b[i]?.id);

const dedupeCreativesById = (items: readonly CreativeEntry[]): CreativeEntry[] => {
  const byId = new Map<string, CreativeEntry>();
  for (const item of items) {
    if (item?.id) {
      byId.set(item.id, item);
    }
  }
  return [...byId.values()];
};

interface Params {
  ad_type?: string;
  network?: string;
  forceEnabled?: boolean;
  with_hypothesis?: boolean;
}

interface UseRowsCreativesReturn {
  rows?: CreativeEntry[];
  selectedRows?: CreativeEntry[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  error?: Record<string, any>;
}

const useRowsCreatives =
  ({
     params,
   }: { params?: Params } = {}): UseRowsCreativesReturn => {
    const { watch, setValue } = useFormContext();
    const app = watch('app') as string;
    const network = watch('network') as string;
    const selectedCreatives = watch('selectedCreatives') as string[];
    const marketingProcess = watch('marketingProcess') as string | undefined;
    const isScalingEgProcess = marketingProcess === 'scaling_eg';
    const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
    const activePacksWithCreatives = watch('activePacksWithCreatives') as
      | number
      | null;
    const packAdType = watch(
      `packsWithCreatives.${activePacksWithCreatives}.adType`,
    ) as string;
    const adType = packAdType || (watch('adType') as string);
    const packFilterCreativeByName = watch(
      `packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`,
    ) as string[];
    const scalingEgFilterCreativeByName = watch('filterCreativesByName') as string[];
    const filterCreativeByName = isScalingEgProcess
      ? scalingEgFilterCreativeByName
      : packFilterCreativeByName;

    const isEnabled = params?.forceEnabled ?? !!adType;
    const withHypothesis = !!params?.with_hypothesis;

    const creativesQuery = useCreatives({
      config: { enabled: isEnabled && !withHypothesis },
      params: {
        app,
        query: filterCreativeByName ?? '',
        ...(params?.ad_type ?? adType ? { ad_type: params?.ad_type ?? adType } : {}),
        network: params?.network ?? network,
      },
    });

    const hypothesisQuery = useCreativesWithHypothesis({
      config: { enabled: isEnabled && withHypothesis },
      params: {
        app,
        query: filterCreativeByName?.join(',') || undefined,
        ad_type: params?.ad_type ?? adType,
        network: params?.network ?? network,
      },
    });

    const hypothesisRows = useMemo(
      () =>
        hypothesisQuery.data
          ? Object.values(hypothesisQuery.data).flat()
          : undefined,
      [hypothesisQuery.data],
    );

    const { isLoading, isSuccess, isError, error } = withHypothesis
      ? hypothesisQuery
      : creativesQuery;
    const rows = withHypothesis ? hypothesisRows : creativesQuery.data;

    const cachedCreativesData = queryClient
      .getQueryCache()
      .findAll({ queryKey: queryKeys.allCreatives })
      .flatMap((query) => query.state.data as CreativeEntry[])
      .filter(Boolean); // Filter out undefined/null values

    const cachedHypothesisCreativesData = dedupeCreativesById(
      queryClient
        .getQueryCache()
        .findAll({ queryKey: queryKeys.allCreativesWithHypothesis })
        .flatMap((query) =>
          Object.values(
            (query.state.data as CreativesByHypothesis | undefined) ?? {},
          ).flat(),
        )
        .filter(Boolean),
    );

    const packIds = useMemo(() => {
      if (isScalingEgProcess) {
        return [];
      }

      if (
        isActivePackIndex(activePacksWithCreatives) &&
        packsWithCreatives?.[activePacksWithCreatives]?.creatives
      ) {
        return packsWithCreatives[activePacksWithCreatives].creatives as string[];
      }
      return [];
    }, [activePacksWithCreatives, isScalingEgProcess, packsWithCreatives]);

    const list = useMemo(
      () => [...new Set([...(packIds ?? []), ...(selectedCreatives ?? [])])],
      [packIds, selectedCreatives],
    );

    const selectedRows = list
      .map(
        (id) =>
          cachedHypothesisCreativesData.find((c) => c?.id === id) ??
          cachedCreativesData.find((c) => c && c?.id === id),
      )
      .filter(Boolean) as CreativeEntry[];

    const [stateRows, setStateRows] = useState<CreativeEntry[]>([]);

    useEffect(() => {
      const filterById = (item: CreativeEntry) =>
        !rows?.some((c) => c && item && c?.id === item?.id);

      let actualRows: CreativeEntry[];
      if (activePacksWithCreatives !== null && !isScalingEgProcess) {
        const findByIdInCache = (id: string) =>
          cachedHypothesisCreativesData.find((c) => c?.id === id) ??
          cachedCreativesData.find((c) => c && c?.id === id);

        const hasNameFilter = (filterCreativeByName?.length ?? 0) > 0;

        if (params?.with_hypothesis && !hasNameFilter) {
          const cachedBaseRows =
            cachedHypothesisCreativesData.length > 0
              ? cachedHypothesisCreativesData
              : cachedCreativesData;
          const selectedPackRows = (packIds ?? [])
            .map(findByIdInCache)
            .filter(Boolean) as CreativeEntry[];
          actualRows = dedupeCreativesById([
            ...cachedBaseRows,
            ...selectedPackRows,
            ...(rows ?? []),
          ]);
        } else {
          const selectedPackRows = packIds
            ?.map(findByIdInCache)
            .filter(Boolean) as CreativeEntry[];
          const keepUniqSelectedRows = selectedPackRows.filter(filterById);
          actualRows = [...keepUniqSelectedRows, ...(rows ?? [])];
        }

        if (!params?.with_hypothesis && !isScalingEgProcess && !shallowEqualStrings(selectedCreatives, packIds ?? [])) {
          setValue('selectedCreatives', packIds ?? []);
        }
      } else {
        const keepUniqSelectedRows = selectedRows.filter(filterById);
        actualRows = [...keepUniqSelectedRows, ...(rows ?? [])];
      }

      // Final safety check to remove any undefined values
      const validRows = actualRows.filter(Boolean);

      if (!sameCreativeRowsById(stateRows, validRows)) {
        setStateRows(validRows);
      }
    }, [
      activePacksWithCreatives,
      filterCreativeByName,
      isScalingEgProcess,
      packIds,
      params?.with_hypothesis,
      rows,
      selectedRows,
      selectedCreatives,
      setValue,
      cachedCreativesData,
      cachedHypothesisCreativesData,
      stateRows,
    ]);

    useEffect(() => {
      if (params?.with_hypothesis) return;
      if (isLoading && stateRows.length) setStateRows([]);
    }, [isLoading, params?.with_hypothesis, stateRows]);

    return {
      rows: stateRows,
      selectedRows,
      isLoading,
      isSuccess,
      isError,
      error: error ?? {},
    };
  };

export default useRowsCreatives;